// src/pages/driverMgmtPage.js
import React, { useEffect, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";

const STATUS_COLORS = {
  available: { bg: '#d1fae5', fg: '#065f46' },
  on_move:   { bg: '#dbeafe', fg: '#1e40af' },
  off_duty:  { bg: '#f3f4f6', fg: '#6b7280' },
};

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [passportNum, setPassportNum] = useState("");
  const [passportExp, setPassportExp] = useState("");
  const [dealerPlate, setDealerPlate] = useState("");
  const [dealerExp, setDealerExp] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [driverStatus, setDriverStatus] = useState("available");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadDrivers(); }, []);

  const loadDrivers = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/driver");
      setDrivers(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load drivers.");
      setDrivers([]);
    } finally {
      setBusy(false);
    }
  };

  const createOrUpdateDriver = async () => {
    if (!fullName.trim()) { setMsg("Full name required."); return; }
    const payload = {
      FullName: fullName, Email: email, Phone: phone, Birthday: birthday,
      PassportNum: passportNum, PassportExp: passportExp,
      DealerPlate: dealerPlate, DealerExp: dealerExp,
      current_location: currentLocation, driver_status: driverStatus,
    };
    setBusy(true); setMsg("");
    try {
      if (editMode) {
        await api.patch(`/driver/${selectedDriver.DriverId}`, payload);
        setMsg("Driver updated.");
      } else {
        await api.post("/driver", payload);
        setMsg("Driver created.");
      }
      resetForm(); loadDrivers(); closeModal();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteDriver = async (DriverId) => {
    if (!window.confirm("Delete this driver?")) return;
    setBusy(true);
    try {
      await api.delete(`/driver/${DriverId}`);
      setMsg("Driver deleted."); loadDrivers();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally { setBusy(false); }
  };

  const openCreateModal = () => { resetForm(); setEditMode(false); setSelectedDriver({}); };

  const openEditModal = (driver) => {
    setFullName(driver.FullName || ""); setEmail(driver.Email || "");
    setPhone(driver.Phone || ""); setBirthday(driver.Birthday || "");
    setPassportNum(driver.PassportNum || ""); setPassportExp(driver.PassportExp || "");
    setDealerPlate(driver.DealerPlate || ""); setDealerExp(driver.DealerExp || "");
    setCurrentLocation(driver.current_location || "");
    setDriverStatus(driver.driver_status || "available");
    setEditMode(true); setSelectedDriver(driver);
  };

  const closeModal = () => { setSelectedDriver(null); setEditMode(false); };

  const resetForm = () => {
    setFullName(""); setEmail(""); setPhone(""); setBirthday("");
    setPassportNum(""); setPassportExp(""); setDealerPlate(""); setDealerExp("");
    setCurrentLocation(""); setDriverStatus("available");
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Driver Management</h2>
        <button className="btn-outline" onClick={loadDrivers} disabled={busy}>Refresh</button>
        <button className="btn" onClick={openCreateModal} disabled={busy}>Add Driver</button>
      </div>

      {msg && <div className="muted" style={{ marginTop: 10 }}>{msg}</div>}

      {/* Status board */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Driver Status Board</div>
        {busy && <div className="muted">Loading…</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {drivers.map((d) => {
            const col = STATUS_COLORS[d.driver_status] || STATUS_COLORS.off_duty;
            return (
              <div key={d.DriverId} style={{ borderRadius: 10, padding: "10px 14px", background: col.bg, color: col.fg }}>
                <div style={{ fontWeight: 900, fontSize: 15 }}>{d.FullName}</div>
                <div style={{ fontSize: 12, marginTop: 2, textTransform: 'uppercase', fontWeight: 700 }}>
                  {d.driver_status || 'unknown'}
                </div>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                  📍 {d.current_location || 'No location set'}
                </div>
              </div>
            );
          })}
          {!busy && drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {/* Full driver list */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>All Drivers</div>
        <div style={{ display: "grid", gap: 10 }}>
          {drivers.map((d) => {
            const col = STATUS_COLORS[d.driver_status] || STATUS_COLORS.off_duty;
            return (
              <div key={d.DriverId} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>{d.FullName}
                      <span style={{ marginLeft: 10, fontSize: 11, padding: '2px 8px', borderRadius: 99, background: col.bg, color: col.fg, fontWeight: 700 }}>
                        {d.driver_status || 'unknown'}
                      </span>
                    </div>
                    <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
                      {d.Email || '—'} • {d.Phone || '—'}
                    </div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      📍 {d.current_location || 'Location not set'} • Plate: {d.DealerPlate || '—'}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <button className="btn-outline" onClick={() => openEditModal(d)}>Edit</button>
                    <button className="btn-outline" onClick={() => deleteDriver(d.DriverId)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
          {!busy && drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {selectedDriver && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit Driver" : "Add Driver"}</h2>
          <div style={{ display: "grid", gap: 10 }}>
            <input className="input" placeholder="Full Name *" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="input" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Birthday" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
              <input className="input" placeholder="Passport #" value={passportNum} onChange={(e) => setPassportNum(e.target.value)} />
              <input className="input" placeholder="Passport Exp" value={passportExp} onChange={(e) => setPassportExp(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Dealer Plate" value={dealerPlate} onChange={(e) => setDealerPlate(e.target.value)} />
              <input className="input" placeholder="Dealer Exp" value={dealerExp} onChange={(e) => setDealerExp(e.target.value)} />
            </div>
            <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Status & Location</div>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <select className="input" value={driverStatus} onChange={(e) => setDriverStatus(e.target.value)}>
                  <option value="available">Available</option>
                  <option value="on_move">On Move</option>
                  <option value="off_duty">Off Duty</option>
                </select>
                <input className="input" placeholder="Current location (e.g. Calgary Yard)" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn" onClick={createOrUpdateDriver} disabled={busy}>{editMode ? "Update" : "Create"}</button>
              <button className="btn-outline" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}